import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageSquare, Mail, Delete, PhoneCall, PhoneOff, PhoneOutgoing, Send, Loader2, Info, ChevronDown, Target } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { StatusBadge } from "./StatusBadge";
import api, { apiError } from "../lib/api";
import { formatDateTime } from "../lib/utils-format";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

function DemoNote({ text }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-secondary/50 border border-border px-3 py-2 text-xs text-muted-foreground">
      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
      <span>{text}</span>
    </div>
  );
}

export function EngagementActions({ lead, onLogged, trigger, hideCard, opportunityId }) {
  const [open, setOpen] = useState(null); // 'call' | 'sms' | 'email'
  const [saving, setSaving] = useState(false);

  const [number, setNumber] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const [callOutcome, setCallOutcome] = useState(null);
  const [cadenceCtx, setCadenceCtx] = useState(null); // { step, workItemId } when call belongs to a cadence step
  const [advanceCadence, setAdvanceCadence] = useState(true);
  const [activeCall, setActiveCall] = useState(null); // { sid, id, mock } while a live call is up
  const [hangingUp, setHangingUp] = useState(false);

  const openCall = () => { setNumber(lead.mobile_phone || lead.business_phone || ""); setCallNotes(""); setCallOutcome(null); setActiveCall(null); setCadenceCtx(null); setAdvanceCadence(true); setOpen("call"); };
  const openSms = () => { setNumber(lead.mobile_phone || ""); setSmsBody(""); setMediaUrl(""); setOpen("sms"); };
  const openEmail = () => { setEmailTo(lead.email || ""); setSubject(`Following up on Arq+`); setEmailBody(""); setOpen("email"); };

  const closeCall = () => { setActiveCall(null); setCallOutcome(null); setCadenceCtx(null); setOpen(null); };
  // Block closing mid-call until a disposition is recorded.
  const guardClose = () => {
    if (activeCall && !callOutcome) { toast.error("Add a call outcome before closing"); return; }
    closeCall();
  };

  // Quick actions triggered from clicking a phone/email field (optionally with cadence context)
  useEffect(() => {
    if (!trigger || !trigger.type) return;
    if (trigger.type === "call") {
      setNumber(trigger.value || ""); setCallNotes(""); setCallOutcome(null); setActiveCall(null); setAdvanceCadence(true);
      setCadenceCtx(trigger.cadenceStep != null || trigger.workItemId ? { step: trigger.cadenceStep, workItemId: trigger.workItemId } : null);
      setOpen("call");
    }
    else if (trigger.type === "sms") { setNumber(trigger.value || ""); setSmsBody(""); setMediaUrl(""); setOpen("sms"); }
    else if (trigger.type === "email") { setEmailTo(trigger.value || ""); setSubject("Following up on Arq+"); setEmailBody(""); setOpen("email"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger?.seq]);

  const startCall = async () => {
    if (!number.trim()) return toast.error("Enter a phone number");
    setSaving(true);
    try {
      const r = await api.post("/telephony/call", {
        lead_id: lead.id, lead_name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
        to: number, notes: callNotes, product_name: lead.product_name,
        cadence_step: cadenceCtx?.step, work_item_id: cadenceCtx?.workItemId, opportunity_id: opportunityId,
      });
      toast.success(r.data?.mock ? "Call started (demo)" : "Call placed via Twilio");
      setActiveCall({ sid: r.data?.provider_sid || null, id: r.data?.id, mock: !!r.data?.mock });
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const endCall = async () => {
    if (!callOutcome) return toast.error("Select a call outcome first");
    setHangingUp(true);
    try {
      const r = await api.post("/telephony/hangup", {
        call_sid: activeCall?.sid, call_id: activeCall?.id, outcome: callOutcome,
        lead_id: lead.id, lead_name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
        cadence_step: cadenceCtx?.step, work_item_id: cadenceCtx?.workItemId,
        advance_cadence: !!cadenceCtx && advanceCadence && typeof cadenceCtx.step === "number", opportunity_id: opportunityId,
      });
      const extra = r.data?.cadence_advanced ? " · cadence moved to next step" : (r.data?.cadence_recorded ? " · cadence updated" : "");
      toast.success(`Call logged — ${callOutcome}${extra}`);
      closeCall(); onLogged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setHangingUp(false); }
  };

  const sendSms = async () => {
    if (!smsBody.trim()) return toast.error("Message cannot be empty");
    if (!number.trim()) return toast.error("Enter a phone number");
    setSaving(true);
    try {
      const r = await api.post("/telephony/sms", {
        lead_id: lead.id, lead_name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
        to: number, body: smsBody, media_url: mediaUrl || null, product_name: lead.product_name, opportunity_id: opportunityId,
      });
      const kind = mediaUrl ? "MMS" : "SMS";
      toast.success(r.data?.mock ? `${kind} logged (demo — Twilio not configured)` : `${kind} sent via Twilio`);
      setOpen(null); onLogged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const sendEmail = async () => {
    if (!emailBody.trim()) return toast.error("Email body cannot be empty");
    if (!emailTo.trim()) return toast.error("Enter a recipient email");
    setSaving(true);
    try {
      const r = await api.post("/email/send", {
        lead_id: lead.id, lead_name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
        to: emailTo, subject, body: emailBody, product_name: lead.product_name, opportunity_id: opportunityId,
      });
      toast.success(r.data?.mock ? "Email logged (demo — Postmark not configured)" : "Email sent via Postmark");
      setOpen(null); onLogged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <>
      {!hideCard && (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="lead-engagement-actions">
        <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Engage</div>
        <div className="flex flex-col gap-2.5">
          <Button className="w-full justify-start" onClick={openCall} data-testid="engage-call-btn"><Phone className="h-4 w-4 mr-2" />Call</Button>
          <Button variant="outline" className="w-full justify-start" onClick={openSms} data-testid="engage-sms-btn"><MessageSquare className="h-4 w-4 mr-2" />Send SMS</Button>
          <Button variant="outline" className="w-full justify-start" onClick={openEmail} data-testid="engage-email-btn"><Mail className="h-4 w-4 mr-2" />Send Email</Button>
        </div>
      </div>
      )}

      {/* Dialer */}
      <Dialog open={open === "call"} onOpenChange={(v) => { if (!v) guardClose(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-[0.12em] flex items-center gap-2.5 text-2xl font-bold">
              <PhoneOutgoing className="h-6 w-6 text-primary" />DIALER
            </DialogTitle>
            <DialogDescription>Place outbound calls and review your call history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Enter a number"
              className="h-16 rounded-2xl bg-secondary/30 border-border text-center text-lg tracking-wide placeholder:text-muted-foreground/70"
              data-testid="dialer-number" />

            <div className="grid grid-cols-3 gap-3">
              {KEYS.map((k) => (
                <button key={k} onClick={() => setNumber((n) => n + k)} data-testid={`dialer-key-${k}`}
                  className="h-16 rounded-2xl border border-border bg-secondary/40 text-2xl font-semibold hover:bg-accent hover:border-primary/40 transition-colors">{k}</button>
              ))}
            </div>

            {activeCall && (
              <div className="space-y-3 rounded-xl border border-border bg-secondary/30 p-3">
                <div className="flex items-center gap-2 text-xs text-emerald-400" data-testid="dialer-in-progress">
                  <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>
                  Call in progress to {number}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Call outcome <span className="text-destructive">*</span></div>
                  <div className="grid grid-cols-3 gap-2">
                    {["Reached", "No Answer", "Voicemail"].map((o) => (
                      <button key={o} onClick={() => setCallOutcome(o)} data-testid={`dialer-outcome-${o.replace(/\s/g, "-").toLowerCase()}`}
                        className={`h-9 rounded-md border text-xs font-medium transition-colors ${callOutcome === o ? "bg-primary text-primary-foreground border-primary" : "border-border bg-secondary/40 hover:bg-accent"}`}>{o}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              {!activeCall ? (
                <Button className="flex-1 h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-base font-semibold tracking-wide" onClick={startCall} disabled={saving} data-testid="dialer-call-btn">
                  {saving ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <PhoneCall className="h-5 w-5 mr-2" />}CALL
                </Button>
              ) : (
                <Button className="flex-1 h-14 rounded-2xl bg-destructive hover:bg-destructive/90 text-white text-base font-semibold" onClick={endCall} disabled={hangingUp || !callOutcome} data-testid="dialer-hangup-btn">
                  {hangingUp ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <PhoneOff className="h-5 w-5 mr-2" />}Hang Up &amp; Log
                </Button>
              )}
              <Button variant="outline" className="h-14 w-14 rounded-2xl shrink-0 p-0" onClick={() => setNumber((n) => n.slice(0, -1))} data-testid="dialer-back">
                <Delete className="h-5 w-5" />
              </Button>
            </div>
            {activeCall && !callOutcome && <div className="text-xs text-muted-foreground text-center">Select a call outcome to end and log this call.</div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS */}
      <Dialog open={open === "sms"} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display tracking-tight">Send SMS</DialogTitle><DialogDescription>Compose an SMS. Sends via Twilio and logs to Engagement History.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>To</Label><Input value={number} onChange={(e) => setNumber(e.target.value)} data-testid="sms-to" /></div>
            <div className="space-y-1.5"><Label>Message</Label><Textarea value={smsBody} onChange={(e) => setSmsBody(e.target.value)} rows={4} placeholder="Type your message…" data-testid="sms-body" /></div>
            <div className="space-y-1.5"><Label>Media URL (optional — sends as MMS)</Label><Input value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} placeholder="https://…/image.jpg" data-testid="sms-media-url" /></div>
            <Button className="w-full" onClick={sendSms} disabled={saving} data-testid="sms-send-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send {mediaUrl ? "MMS" : "SMS"}
            </Button>
            <DemoNote text="Sends a real SMS/MMS via Twilio to the number above. Delivered messages are logged to history." />
          </div>
        </DialogContent>
      </Dialog>

      {/* Email */}
      <Dialog open={open === "email"} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display tracking-tight">Send Email</DialogTitle><DialogDescription>Compose an email. Sends via Postmark and logs to Engagement History.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>To</Label><Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="email-to" /></div>
            <div className="space-y-1.5"><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" /></div>
            <div className="space-y-1.5"><Label>Body</Label><Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={5} placeholder="Write your email…" data-testid="email-body" /></div>
            <Button className="w-full" onClick={sendEmail} disabled={saving} data-testid="email-send-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send Email
            </Button>
            <DemoNote text="Sends a real email via Postmark from hello@arqplus.co. Sent emails are logged to history." />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const CH_ICON = { Phone: Phone, SMS: MessageSquare, Email: Mail };

export function EngagementHistory({ leadId, refreshToken }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/calls?lead_id=${leadId}&limit=100`).then((r) => r.data.items).catch(() => []),
      api.get(`/messages?lead_id=${leadId}&limit=100`).then((r) => r.data.items).catch(() => []),
    ]).then(([calls, msgs]) => {
      const merged = [...calls, ...msgs].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setItems(merged);
    }).finally(() => setLoading(false));
  }, [leadId, refreshToken]);

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="engagement-history">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/40 transition-colors"
        data-testid="engagement-history-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Engagement History</span>
          <span className="text-xs text-muted-foreground">({items.length})</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5" data-testid="engagement-history-body">
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No calls, SMS, or emails logged yet. Use the Engage actions.</p>
          ) : (
            <div className="space-y-3">
              {items.map((it) => {
                const Icon = CH_ICON[it.channel] || MessageSquare;
                const isCall = it.channel === "Phone";
                return (
                  <div key={it.id} className="flex gap-3 pb-3 border-b border-border/50 last:border-0 last:pb-0" data-testid={`history-item-${it.id}`}>
                    <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-primary" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{it.channel}</span>
                        <span className="text-xs text-muted-foreground capitalize">· {it.direction}</span>
                        {it.status && <StatusBadge value={it.status} />}
                      </div>
                      {isCall ? (
                        <div className="text-sm text-muted-foreground mt-0.5">
                          {it.to_number}{it.outcome ? ` · ${it.outcome}` : ""}{it.notes ? ` — ${it.notes}` : ""}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground mt-0.5 break-words">
                          {it.subject ? <span className="font-medium text-foreground">{it.subject}: </span> : null}{it.body}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">{formatDateTime(it.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
